import React, { useState, useContext, useEffect, useMemo } from "react";
import * as M from "./ModalStyle";
import { AuthContext } from "../../../context/AuthContext";
import { db } from "../../../database/firebaseConfig";
import { usePulse } from "../../../context/LoadContext";
import { doc, getDoc } from "firebase/firestore";
import axios from "axios";
import { formatNumber } from "../../../assets/utils";
import { FiX, FiAlertTriangle } from "react-icons/fi";
import { useMessage } from "../../../context/MessageContext";

export default function Modal({ handleModalSaque, contracts }) {
  const { userData, reloadUserData, token } = useContext(AuthContext);
  const { showPulse, hidePulse } = usePulse();
  const { showMessage } = useMessage();


  const [valorSolicitado, setValorSolicitado] = useState("");
  const [withdrawType, setWithdrawType] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isWindowActive, setIsWindowActive] = useState(true);

  const TAXA_SAQUE = 0.04;
  const VALOR_MINIMO_SAQUE = 150.0;

  useEffect(() => {
    const fetchWithdrawWindowState = async () => {
      const docRef = doc(db, "SYSTEM_VARIABLES", "JANELA_DE_SAQUES");
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setIsWindowActive(docSnap.data().ACTIVE);
      }
    };
    fetchWithdrawWindowState();
  }, []);

  const saldoDisponivel = useMemo(() => {
    if (withdrawType === "indication") {
      return parseFloat(userData.sponsorCredit) || 0;
    }
    if (withdrawType === "contract" && selectedContract) {
      return parseFloat(selectedContract.avaliableToWithdraw) || 0;
    }
    return 0;
  }, [withdrawType, selectedContract, userData]);

  const valorSolicitadoFloat = parseFloat(valorSolicitado.replace(/\./g, '').replace(',', '.')) || 0;
  const valorTaxa = valorSolicitadoFloat * TAXA_SAQUE;
  const valorRecebivel = valorSolicitadoFloat - valorTaxa;

  const isSaqueDisponivel =
    valorSolicitadoFloat >= VALOR_MINIMO_SAQUE &&
    valorSolicitadoFloat <= saldoDisponivel &&
    !isProcessing;

  const handleInputChange = (e) => {
    let value = e.target.value;
    value = value.replace(/[^0-9,]/g, "");
    const parts = value.split(',');
    if (parts.length > 2) {
      value = parts[0] + ',' + parts.slice(1).join('');
    }
    setValorSolicitado(value);
  };

  const handleSelectType = (type) => {
    setWithdrawType(type);
    setSelectedContract(null);
    setValorSolicitado("");
  };
  
  const handleSelectContract = (contract) => {
    setSelectedContract(contract);
    setValorSolicitado("");
  };

  const handleSacar = async () => {
    if (!isSaqueDisponivel) {
      if (valorSolicitadoFloat < VALOR_MINIMO_SAQUE) {
        showMessage(`O valor mínimo para saque é R$ ${formatNumber(VALOR_MINIMO_SAQUE)}.`, 'warning');
      } else if (valorSolicitadoFloat > saldoDisponivel) {
        showMessage('O valor solicitado é maior que o saldo disponível.', 'error');
      }
      return;
    }

    setIsProcessing(true);
    showPulse();

    try {
      const saqueData = {
        clientId: userData.id,
        amountWithdrawn: valorSolicitadoFloat,
        contractId: withdrawType === "contract" && selectedContract ? selectedContract.contract.id : null,
        sponsorWithdraw: withdrawType === "indication",
      };

      const res = await axios.post(
        `${process.env.REACT_APP_BASE_ROUTE}withdraw`,
        saqueData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Status 201 é o CreatedAtAction do seu Controller
      if (res.status === 201) {
        showMessage("Saque solicitado com sucesso!", 'success');
        await reloadUserData();
        handleModalSaque();
      }
    } catch (error) {
      // O PULO DO GATO ESTÁ AQUI:
      // Se o backend retornaBadRequest("mensagem"), o axios coloca a string em error.response.data
      const errorMsg = error.response?.data || "Ocorreu um erro ao processar o saque.";
      
      // Se por acaso o backend retornar um objeto JSON { message: "..." }, tratamos também:
      const finalMsg = typeof errorMsg === 'string' ? errorMsg : errorMsg.message;

      showMessage(finalMsg, 'error');
      console.error("Erro no saque:", error);
    } finally {
      setIsProcessing(false);
      hidePulse();
    }
  };
  

  const renderContent = () => {
    if (!isWindowActive) {
      return (
        <M.DisabledMessage>
          <FiAlertTriangle size={48} />
          <p>As solicitações de saque estão temporariamente desativadas.</p>
        </M.DisabledMessage>
      );
    }

    return (
      <>
        <M.Section>
          <M.SectionTitle>1. Escolha a origem do saque</M.SectionTitle>
          <M.ButtonGroup>
            <M.TypeButton isSelected={withdrawType === 'indication'} onClick={() => handleSelectType('indication')}>
              Saldo de Indicação
            </M.TypeButton>
            <M.TypeButton isSelected={withdrawType === 'contract'} onClick={() => handleSelectType('contract')}>
              Saldo de Contratos
            </M.TypeButton>
          </M.ButtonGroup>
        </M.Section>

        {withdrawType === 'indication' && (
          <M.InfoBox>
            <span>Saldo Disponível</span>
            <strong>R$ {formatNumber(saldoDisponivel)}</strong>
          </M.InfoBox>
        )}

        {withdrawType === 'contract' && (
          <M.Section>
            <M.SectionTitle>2. Selecione o contrato</M.SectionTitle>
            <M.ContractList>
              {contracts && contracts.length > 0 ? (
                contracts.map((ctr) => (
                  <M.ContractItem key={ctr.contract.id} isSelected={selectedContract?.contract.id === ctr.contract.id} onClick={() => handleSelectContract(ctr)}>
                    <span>Cód: #{ctr.contract.id}</span>
                    <span>Total: R$ {formatNumber(ctr.contract.totalPrice)}</span>
                    <span>Disponível: R$ {formatNumber(ctr.avaliableToWithdraw)}</span>
                  </M.ContractItem>
                ))
              ) : (
                <p style={{textAlign: 'center', padding: '1rem'}}>Nenhum contrato com saldo disponível.</p>
              )}
            </M.ContractList>
            {selectedContract && (
                <M.InfoBox>
                    <span>Disponível no Contrato #{selectedContract.contract.id}</span>
                    <strong>R$ {formatNumber(saldoDisponivel)}</strong>
                </M.InfoBox>
            )}
          </M.Section>
        )}

        {(saldoDisponivel > 0 || valorSolicitado) && (withdrawType === 'indication' || selectedContract) && (
          <M.Section>
            <M.SectionTitle>3. Defina o valor</M.SectionTitle>
            <M.InputGroup>
              <label htmlFor="valor-saque">Digite o valor (mínimo R$ {formatNumber(VALOR_MINIMO_SAQUE)})</label>
              <M.Input
                id="valor-saque"
                value={valorSolicitado}
                onChange={handleInputChange}
                placeholder="0,00"
              />
            </M.InputGroup>
            {valorSolicitadoFloat > 0 && (
              <M.Summary>
                <div><span>Valor Solicitado:</span> <span>R$ {formatNumber(valorSolicitadoFloat)}</span></div>
                <div><span>Taxa de Saque (4%):</span> <span>- R$ {formatNumber(valorTaxa)}</span></div>
                <div><span>Valor a Receber:</span> <span className="net-value">R$ {formatNumber(valorRecebivel)}</span></div>
              </M.Summary>
            )}
          </M.Section>
        )}
      </>
    );
  };

  return (
    <M.ModalOverlay>
      <M.ModalWrapper>
        <M.ModalHeader>
          <h2>Solicitação de Saque</h2>
          <M.CloseButton onClick={handleModalSaque}><FiX /></M.CloseButton>
        </M.ModalHeader>
        <M.ModalBody>
          {renderContent()}
        </M.ModalBody>
        {isWindowActive && (
          <M.ModalFooter>
            <M.ActionButton variant="secondary" onClick={handleModalSaque}>Cancelar</M.ActionButton>
            <M.ActionButton variant="primary" onClick={handleSacar} disabled={!isSaqueDisponivel}>
              {isProcessing ? "Processando..." : "Confirmar Saque"}
            </M.ActionButton>
          </M.ModalFooter>
        )}
      </M.ModalWrapper>
    </M.ModalOverlay>
  );
}